/* eslint-disable react-hooks/exhaustive-deps */
import {FC, useContext, useState, useEffect, useMemo} from 'react'
import {useQuery} from 'react-query'
import {
  createResponseContext,
  initialQueryResponse,
  initialQueryState,
  isNotEmpty,
  PaginationState,
  QUERIES,
  stringifyRequestQuery,
  WithChildren,
} from '../../../../../../_metronic/helpers'
import {getContracts, searchContract} from './_requests'
import {Contracts} from './_models'
import {useQueryRequest} from './QueryRequestProvider'

const QueryResponseContext = createResponseContext<Contracts>(initialQueryResponse)
const QueryResponseProvider: FC<WithChildren> = ({children}) => {
  const {state} = useQueryRequest()
  const [query, setQuery] = useState<string>(stringifyRequestQuery(state))
  const updatedQuery = useMemo(() => stringifyRequestQuery(state), [state])

  useEffect(() => {
    if (query !== updatedQuery) {
      setQuery(updatedQuery)
    }
  }, [updatedQuery])

  const {
    isFetching,
    isLoading,
    refetch,
    data: response,
  } = useQuery(
      `${QUERIES.CONTRACTS_LIST}-${query}`,
      () => {
        if (isNotEmpty(state.search)) {
          return searchContract(state)
        } else {
          return getContracts(query)
        }
      },
      {
        cacheTime: 0,
        keepPreviousData: true,
        refetchOnWindowFocus: true,
        refetchInterval: (data) => {
          const items = data?.data ?? []
          const hasActiveAi = items.some(
            (c: Contracts) =>
              c.ai_analysis?.status === 'queued' || c.ai_analysis?.status === 'processing'
          )
          return hasActiveAi ? 5000 : false
        },
      }
  )

  return (
      <QueryResponseContext.Provider value={{isLoading, refetch, response, query}}>
        {children}
      </QueryResponseContext.Provider>
  )
}

const useQueryResponse = () => useContext(QueryResponseContext)

const useQueryResponseData = () => {
  const {response} = useQueryResponse()

  if (!response) {
    return []
  }

  return response?.data || []
}

const useQueryResponsePagination = () => {
  const defaultPaginationState: PaginationState = {
    links: [],
    ...initialQueryState,
  }

  const {response} = useQueryResponse()
  if (!response || !response.payload || !response.payload.pagination) {
    return defaultPaginationState
  }

  return response.payload.pagination
}

const useQueryResponseLoading = (): boolean => {
  const {isLoading} = useQueryResponse()
  return isLoading
}

export {
  QueryResponseProvider,
  useQueryResponse,
  useQueryResponseData,
  useQueryResponsePagination,
  useQueryResponseLoading,
}
